import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const links = [
  { to: "/produit", key: "public_nav.product" },
  { to: "/tarifs", key: "public_nav.pricing" },
  { to: "/faq", key: "public_nav.faq" },
  { to: "/a-propos", key: "public_nav.about" },
  { to: "/contact", key: "public_nav.contact" },
];

export default function PublicHeader() {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <motion.header
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.4 }}
      className={`sticky top-0 z-40 transition-all ${scrolled ? "backdrop-blur-lg bg-bg/85 border-b border-c" : "bg-transparent"}`}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-3">
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="header-logo">
          <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl bg-primary-c flex items-center justify-center text-white font-display text-base sm:text-lg">O</div>
          <span className="font-display text-lg sm:text-xl">Oresto</span>
          <span className="hidden md:inline text-[10px] font-semibold uppercase tracking-wider text-secondary-c bg-secondary-c/10 px-2 py-0.5 rounded-full">{t("nav.swiss_badge")}</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1" data-testid="public-nav">
          {links.map((l) => {
            const active = location.pathname === l.to;
            return (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.to.replace(/\//g, "")}`}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${active ? "bg-surface-muted text-[var(--text)]" : "text-text-secondary hover:text-[var(--text)]"}`}
              >
                {t(l.key)}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-1.5 shrink-0">
          <LanguageSwitcher />
          <Link to="/login" data-testid="nav-login-link" className="hidden sm:inline-block text-sm font-medium text-text-secondary hover:text-[var(--text)] px-2 py-1">{t("nav.login")}</Link>
          <Link to="/register" data-testid="nav-cta-register" className="btn-primary text-sm !py-2 !px-3 sm:!px-4">{t("nav.cta_register")}</Link>
          <button
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden p-2 -mr-1 text-text-secondary hover:text-[var(--text)]"
            data-testid="mobile-menu-toggle"
            aria-label="menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          className="lg:hidden border-t border-c bg-bg overflow-hidden"
          data-testid="mobile-nav"
        >
          <nav className="px-4 py-3 flex flex-col">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="py-2.5 text-sm font-medium text-text-secondary hover:text-primary-c"
                data-testid={`mobile-nav-${l.to.replace(/\//g, "")}`}
              >
                {t(l.key)}
              </Link>
            ))}
            <Link to="/login" className="py-2.5 text-sm font-medium text-text-secondary hover:text-primary-c">{t("nav.login")}</Link>
          </nav>
        </motion.div>
      )}
    </motion.header>
  );
}
